import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';



const WaveformPlayer = ({ audioUrl, currentTime, duration, isPlaying, onSeek }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [waveformData, setWaveformData] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverX, setHoverX] = useState(0);
  const { currentTheme } = useTheme();
  const animFrameRef = useRef(null);
  const cachedUrlRef = useRef(null);

  // Generate waveform data from audio
  useEffect(() => {
    if (!audioUrl || audioUrl === cachedUrlRef.current) return;
    cachedUrlRef.current = audioUrl;

    const generateWaveform = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        const channelData = audioBuffer.getChannelData(0);
        const bars = 120; // Number of bars in waveform
        const samplesPerBar = Math.floor(channelData.length / bars);
        const peaks = [];
        
        for (let i = 0; i < bars; i++) {
          let sum = 0;
          const start = i * samplesPerBar;
          for (let j = start; j < start + samplesPerBar && j < channelData.length; j++) {
            sum += Math.abs(channelData[j]);
          }
          peaks.push(sum / samplesPerBar);
        }
        
        // Normalize
        const max = Math.max(...peaks);
        const normalized = peaks.map(p => p / (max || 1));
        
        setWaveformData(normalized);
        audioContext.close();
      } catch (err) {
        console.error('Waveform generation failed:', err);
        // Fallback: generate random-ish waveform
        const bars = 120;
        const fallback = Array.from({ length: bars }, (_, i) => {
          const x = i / bars;
          return 0.3 + 0.7 * Math.abs(Math.sin(x * Math.PI * 3) * Math.cos(x * 7) + Math.sin(x * 13) * 0.3);
        });
        setWaveformData(fallback);
      }
    };

    generateWaveform();
  }, [audioUrl]);

  // Draw waveform
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !waveformData) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    const barCount = waveformData.length;
    const barWidth = rect.width / barCount;
    const gap = Math.max(1, barWidth * 0.2);
    const actualBarWidth = barWidth - gap;
    const progress = duration > 0 ? currentTime / duration : 0;
    const progressX = progress * rect.width;
    const hoverProgress = isHovering ? hoverX / rect.width : -1;

    const centerY = rect.height / 2;
    const maxBarHeight = rect.height * 0.85;

    for (let i = 0; i < barCount; i++) {
      const x = i * barWidth + gap / 2;
      const barHeight = Math.max(2, waveformData[i] * maxBarHeight);
      const halfBar = barHeight / 2;
      const barX = x + (i * barWidth < progressX ? 0 : 0);

      // Color based on playback position
      if (x + actualBarWidth <= progressX) {
        // Played — theme color with glow
        ctx.fillStyle = currentTheme.primary;
        ctx.shadowColor = currentTheme.primary;
        ctx.shadowBlur = 3;
      } else if (isHovering && x <= hoverX) {
        // Hover preview — lighter theme color
        ctx.fillStyle = currentTheme.primary + '80';
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      } else {
        // Unplayed — dim white
        ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      // Draw bar — plain fillRect, works on all mobile browsers
      ctx.fillRect(barX, centerY - halfBar, actualBarWidth, halfBar);
      ctx.fillRect(barX, centerY, actualBarWidth, halfBar * 0.7);
    }

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Playhead line
    if (progress > 0 && progress < 1) {
      ctx.beginPath();
      ctx.strokeStyle = currentTheme.primary;
      ctx.lineWidth = 2;
      ctx.moveTo(progressX, 2);
      ctx.lineTo(progressX, rect.height - 2);
      ctx.stroke();

      // Playhead dot
      ctx.beginPath();
      ctx.fillStyle = currentTheme.primary;
      ctx.arc(progressX, centerY, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }, [waveformData, currentTime, duration, currentTheme, isHovering, hoverX]);

  // Animation loop for smooth playback visualization
  useEffect(() => {
    const animate = () => {
      try {
        drawWaveform();
      } catch (err) {
        console.warn('Waveform draw error (suppressed):', err);
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [drawWaveform]);

  // Handle click to seek
  const handleClick = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = x / rect.width;
    const seekTime = progress * (duration || 0);
    onSeek(seekTime);
  };

  const handleMouseMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    setHoverX(e.clientX - rect.left);
  };

  // Format hover time
  const getHoverTime = () => {
    if (!isHovering || !containerRef.current) return '';
    const rect = containerRef.current.getBoundingClientRect();
    const progress = hoverX / rect.width;
    const time = progress * (duration || 0);
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full cursor-pointer select-none"
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={handleMouseMove}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
      />
      {/* Hover time tooltip */}
      {isHovering && duration > 0 && (
        <div
          className="absolute top-0 transform -translate-x-1/2 -translate-y-full px-2 py-1 rounded text-xs font-bold text-black pointer-events-none"
          style={{ 
            left: `${hoverX}px`,
            backgroundColor: currentTheme.primary,
            marginTop: '-4px'
          }}
        >
          {getHoverTime()}
        </div>
      )}
      {/* Loading state */}
      {!waveformData && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-gray-500 text-xs animate-pulse">Loading waveform...</div>
        </div>
      )}
    </div>
  );
};

export default WaveformPlayer;
